import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FileText, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#002255', '#FF6600', '#3B82F6', '#10B981', '#F59E0B'];

const Rapports = () => {
  const [stats, setStats] = useState(null);
  const [zoneStats, setZoneStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [dashboardResponse, zoneResponse] = await Promise.all([
        axios.get(`${API}/stats/dashboard`, { withCredentials: true }),
        axios.get(`${API}/stats/zone-geographique`, { withCredentials: true })
      ]);
      setStats(dashboardResponse.data);
      setZoneStats(zoneResponse.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const generatePDF = () => {
    const doc = new jsPDF();
    
    // Title
    doc.setFontSize(20);
    doc.setTextColor(0, 34, 85);
    doc.text('Rapport CRM Webuildd', 14, 20);
    
    doc.setFontSize(10);
    doc.setTextColor(100, 116, 139);
    doc.text(`Généré le ${new Date().toLocaleDateString('fr-FR')}`, 14, 28);
    
    // Summary Stats
    doc.setFontSize(14);
    doc.setTextColor(0, 34, 85);
    doc.text('Statistiques Générales', 14, 40);
    
    const summaryData = [
      ['Total Contacts', stats?.total_contacts || 0],
      ['Total Opportunités', stats?.total_opportunites || 0],
      ['Opportunités Chaudes', stats?.opportunites_chaudes || 0],
      ['Opportunités Gagnées', stats?.opportunites_gagnees || 0],
      ['Montant Total Estimé', `${new Intl.NumberFormat('fr-FR').format(stats?.montant_total_estime || 0)} FCFA`]
    ];
    
    doc.autoTable({
      startY: 45,
      head: [['Indicateur', 'Valeur']],
      body: summaryData,
      theme: 'grid',
      headStyles: { fillColor: [0, 34, 85] },
    });
    
    // Status Distribution
    let currentY = doc.lastAutoTable.finalY + 10;
    doc.setFontSize(14);
    doc.text('Répartition par Statut', 14, currentY);
    
    if (stats?.repartition_statuts && stats.repartition_statuts.length > 0) {
      const statusData = stats.repartition_statuts.map(s => [s._id, s.count]);
      doc.autoTable({
        startY: currentY + 5,
        head: [['Statut', 'Nombre']],
        body: statusData,
        theme: 'grid',
        headStyles: { fillColor: [0, 34, 85] },
      });
      currentY = doc.lastAutoTable.finalY + 10;
    }
    
    // Zone Geographic
    if (zoneStats?.zones && zoneStats.zones.length > 0) {
      doc.setFontSize(14);
      doc.text('Répartition par Zone Géographique', 14, currentY);
      
      const zoneData = zoneStats.zones.map(z => [
        z._id,
        z.count,
        `${new Intl.NumberFormat('fr-FR').format(z.montant_total)} FCFA`
      ]);
      
      doc.autoTable({
        startY: currentY + 5,
        head: [['Zone', 'Nombre', 'Montant Total']],
        body: zoneData,
        theme: 'grid',
        headStyles: { fillColor: [0, 34, 85] },
      });
    }
    
    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(100, 116, 139);
      doc.text(
        `Page ${i} sur ${pageCount}`,
        doc.internal.pageSize.getWidth() / 2,
        doc.internal.pageSize.getHeight() - 10,
        { align: 'center' }
      );
      doc.text(
        '© 2026 Webuildd Foncier et Immobilier',
        14,
        doc.internal.pageSize.getHeight() - 10
      );
    }
    
    doc.save(`rapport-webuildd-${new Date().toISOString().split('T')[0]}.pdf`);
    toast.success('Rapport PDF généré avec succès');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#002255]"></div>
      </div>
    );
  }

  return (
    <div data-testid="rapports-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-[#002255] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Rapports & Analytics
          </h1>
          <p className="text-gray-600">Analysez vos performances commerciales</p>
        </div>
        <Button
          onClick={generatePDF}
          className="bg-[#FF6600] hover:bg-[#FF6600]/90 text-white"
          data-testid="generate-pdf-button"
        >
          <Download className="w-4 h-4 mr-2" />
          Générer PDF
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="stat-card">
          <p className="text-gray-600 text-sm mb-1">Total Contacts</p>
          <p className="text-3xl font-bold text-[#002255]">{stats?.total_contacts || 0}</p>
        </div>
        <div className="stat-card">
          <p className="text-gray-600 text-sm mb-1">Total Opportunités</p>
          <p className="text-3xl font-bold text-[#002255]">{stats?.total_opportunites || 0}</p>
        </div>
        <div className="stat-card">
          <p className="text-gray-600 text-sm mb-1">Taux de Conversion</p>
          <p className="text-3xl font-bold text-[#002255]">
            {stats?.total_opportunites > 0
              ? Math.round((stats.opportunites_gagnees / stats.total_opportunites) * 100)
              : 0}%
          </p>
        </div>
        <div className="stat-card">
          <p className="text-gray-600 text-sm mb-1">Montant Total</p>
          <p className="text-2xl font-bold text-[#002255]">
            {new Intl.NumberFormat('fr-FR', {
              notation: 'compact',
              compactDisplay: 'short'
            }).format(stats?.montant_total_estime || 0)} FCFA
          </p>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Status Chart */}
        <div className="stat-card">
          <h3 className="text-lg font-semibold text-[#002255] mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Répartition par Statut
          </h3>
          {stats?.repartition_statuts && stats.repartition_statuts.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={stats.repartition_statuts}
                  dataKey="count"
                  nameKey="_id"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  label
                >
                  {stats.repartition_statuts.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-gray-500 text-center py-12">Aucune donnée</p>
          )}
        </div>

        {/* Category Chart */}
        <div className="stat-card">
          <h3 className="text-lg font-semibold text-[#002255] mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Répartition par Catégorie
          </h3>
          {stats?.repartition_categories && stats.repartition_categories.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={stats.repartition_categories}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="_id" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="count" fill="#002255" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-gray-500 text-center py-12">Aucune donnée</p>
          )}
        </div>
      </div>

      {/* Zone Geographic */}
      {zoneStats?.zones && zoneStats.zones.length > 0 && (
        <div className="stat-card">
          <h3 className="text-lg font-semibold text-[#002255] mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Opportunités par Zone Géographique
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Zone</th>
                  <th className="text-right py-3 px-4 font-semibold text-gray-700">Nombre</th>
                  <th className="text-right py-3 px-4 font-semibold text-gray-700">Montant Total</th>
                </tr>
              </thead>
              <tbody>
                {zoneStats.zones.map((zone, index) => (
                  <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4">{zone._id}</td>
                    <td className="text-right py-3 px-4">{zone.count}</td>
                    <td className="text-right py-3 px-4 font-medium">
                      {new Intl.NumberFormat('fr-FR', {
                        style: 'currency',
                        currency: 'XOF',
                        minimumFractionDigits: 0
                      }).format(zone.montant_total)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Rapports;
